import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  faBell,
  faFloppyDisk,
  faGear,
  faHandWave,
  faHouse,
  faPartyHorn,
  faScroll,
  faTag,
  faTruck,
  faWandMagicSparkles,
} from '@fortawesome/pro-duotone-svg-icons'

import { useAuth } from '../../_hooks/use-auth'
import { useCelebration } from '../../_hooks/use-celebration'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useKonamiCode } from '../../_hooks/use-konami-code'
import { useQuickPassNotifications } from '../../_hooks/use-quickpass-notifications'
import { useTheme } from '../../_hooks/use-theme'
import HotkeysCheatsheet from '../hotkeys-cheatsheet/HotkeysCheatsheet'
import CommandPalette from './CommandPalette'
import { nextThemeLabelFor, themeGlyphFor } from './helpers'
import { CommandEntry } from './types'

/**
 * Listens for Cmd+K (or Ctrl+K) globally and renders the command palette
 * with a context-aware entry list — every route, every imported listing,
 * plus a couple of action commands (open notifications, sign out).
 *
 * Mounts at the router level so `useNavigate` works for navigation
 * commands. No-op when the renter is signed out — the palette is only
 * useful inside the app.
 *
 * @returns {JSX.Element}
 */
const CommandPaletteHost = (): JSX.Element => {
  const navigate = useNavigate()
  const { isAuthenticated, signOut } = useAuth()
  const { listings } = useImportedListings()
  const { openDrawer } = useQuickPassNotifications()
  const { celebrate } = useCelebration()
  const { theme, cycleTheme } = useTheme()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isCheatsheetOpen, setIsCheatsheetOpen] = useState<boolean>(false)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const isPaletteShortcut =
        (event.metaKey === true || event.ctrlKey === true) &&
        event.key.toLowerCase() === 'k'

      if (isPaletteShortcut === true) {
        event.preventDefault()
        setIsOpen(previous => previous === false)
        return
      }

      const target = event.target as HTMLElement | null
      const isTypingInField =
        target !== null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable === true)

      if (event.key === '?' && isTypingInField === false) {
        event.preventDefault()
        setIsCheatsheetOpen(previous => previous === false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleKonami = useCallback((): void => {
    celebrate()
    setTimeout(() => celebrate(), 280)
    setTimeout(() => celebrate(), 560)
    // eslint-disable-next-line no-console -- intentional easter-egg flavour, gated behind the konami code
    console.info(
      '⬆️⬆️⬇️⬇️⬅️➡️⬅️➡️ B A — you absolute legend. Triple confetti. No prize. Sorry.',
    )
  }, [celebrate])

  useKonamiCode(handleKonami)

  const handleClose = (): void => {
    setIsOpen(false)
  }

  const handleCheatsheetClose = (): void => {
    setIsCheatsheetOpen(false)
  }

  const entries = useMemo<CommandEntry[]>(() => {
    if (isAuthenticated === false) {
      return []
    }

    const navigationEntries: CommandEntry[] = [
      {
        id: 'go-dashboard',
        label: 'Dashboard',
        hint: 'Home base — greeting, insights, quick actions',
        glyph: faHouse,
        keywords: ['home', 'main', 'overview', 'greeting'],
        onRun: () => navigate('/dashboard'),
      },
      {
        id: 'go-saved-listings',
        label: 'Saved listings',
        hint: "All places you're tracking",
        glyph: faFloppyDisk,
        keywords: ['imports', 'imported', 'tracked', 'watched'],
        onRun: () => navigate('/saved-listings'),
      },
      {
        id: 'go-profile',
        label: 'Profile',
        hint: 'Your preferences + identity',
        glyph: faGear,
        keywords: ['preferences', 'settings', 'me'],
        onRun: () => navigate('/profile'),
      },
      {
        id: 'go-onboarding',
        label: 'Re-run onboarding wizard',
        hint: 'Walk through preferences from scratch',
        glyph: faWandMagicSparkles,
        keywords: ['restart', 'wizard', 'walkthrough'],
        onRun: () => navigate('/onboarding'),
      },
      {
        id: 'go-activity',
        label: 'Activity feed',
        hint: 'Chronological log of everything that happened',
        glyph: faScroll,
        keywords: ['history', 'feed', 'timeline', 'log'],
        onRun: () => navigate('/activity'),
      },
      {
        id: 'go-move-prep',
        label: 'Move-prep checklist',
        hint: 'Countdown + tasks to do before move-in',
        glyph: faTruck,
        keywords: ['moving', 'todo', 'checklist', 'tasks', 'utilities'],
        onRun: () => navigate('/move-prep'),
      },
    ]

    const themeGlyph = themeGlyphFor(theme)
    const nextThemeLabel = nextThemeLabelFor(theme)

    const actionEntries: CommandEntry[] = [
      {
        id: 'open-notifications',
        label: 'Open notifications',
        hint: 'Show the activity drawer',
        glyph: faBell,
        keywords: ['drawer', 'updates', 'alerts'],
        onRun: () => openDrawer(),
      },
      {
        id: 'cycle-theme',
        label: `Switch theme → ${nextThemeLabel}`,
        hint: 'Light, cozy peach, or midnight purple.',
        glyph: themeGlyph,
        keywords: ['dark', 'light', 'cozy', 'mode', 'theme'],
        onRun: () => cycleTheme(),
      },
      {
        id: 'celebrate',
        label: 'Throw confetti',
        hint: 'Just because.',
        glyph: faPartyHorn,
        keywords: ['party', 'celebrate', 'fun'],
        onRun: () => celebrate(),
      },
      {
        id: 'sign-out',
        label: 'Sign out',
        hint: 'End your session',
        glyph: faHandWave,
        keywords: ['logout', 'leave', 'exit'],
        onRun: async () => {
          try {
            await signOut()
            navigate('/sign-in')
          } catch (error) {
            console.error('Sign-out failed', error)
          }
        },
      },
    ]

    const listingEntries: CommandEntry[] = listings.map(listing => ({
      id: `go-listing-${listing.id}`,
      label: listing.title,
      hint: `Open detail · ${listing.city || 'Unknown city'}`,
      glyph: faTag,
      keywords: [
        listing.city,
        listing.sourceSite,
        listing.fullAddress ?? '',
      ].filter(value => value !== ''),
      onRun: () => navigate(`/imported-listings/${listing.id}`),
    }))

    return [...navigationEntries, ...actionEntries, ...listingEntries]
  }, [
    isAuthenticated,
    listings,
    navigate,
    openDrawer,
    celebrate,
    signOut,
    theme,
    cycleTheme,
  ])

  return (
    <>
      <CommandPalette isOpen={isOpen} onClose={handleClose} entries={entries} />
      <HotkeysCheatsheet
        isOpen={isCheatsheetOpen}
        onClose={handleCheatsheetClose}
      />
    </>
  )
}

export default CommandPaletteHost
